import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '@SiteComponents/Button'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import Close from '@Images/bulk_action_bar_close.svg'
import Add from '@Images/bulk_action_bar_add.svg'
import Delete from '@Images/bulk_action_bar_delete.svg'
import EditLabel from '@Images/bulk_action_bar_edit_label.svg'
import Exportation from '@Images/bulk_action_bar_exportation.svg'
import Move from '@Images/bulk_action_bar_move.svg'
import More from '@Images/finetune_parameters_more.svg'
import Share from '@Images/share.svg'

import {
  unselectProjects,
} from '@SiteModules/Project/actions'

import {
  openDeleteProjectPopup,
  openLabelsManagerPopup,
  openSetupProjectPopup,
  openSetupProjectPopupTts,
  openMoveToOtherWorkspacePopup,
  openRenewSubscriptionPopup,
  openExportationPopup,
  openProjectSharingPopup,
} from '@SiteModules/Popup/actions'

import {
  getSelectedProjectIds,
  getProjectsWithLanguageSelected,
  getAllSelectedProjectsAllowToAddLanguages,
  getAllSelectedProjectsAllowToGenerateClips,
  getAllSelectedProjectsBelongToTtsProjects,
  getAllSelectedProjectsCanManageSharing,
  getOriginalProjectVersionIdsOfSelectedProjects,
} from '@SiteModules/Project/selectors'

import {
  getWorkspaces,
} from '@SiteModules/Workspace/selectors'

import {
  getUserIsAccountAdmin,
  getCurrentAccountHasCurrentSubscription,
} from '@SiteModules/Account/selectors'

import './ProjectsBulkActionBar.scss'

const BAR_WIDTH = 911 // px, as in ProjectsBulkActionBar width

const ProjectsBulkActionBar: React.FC = () => {
  const dispatch = useDispatch()

  const [showMoreDropdown, setShowMoreDropdown] = useState(false)

  const selectedProjectIds = useSelector(getSelectedProjectIds)
  const originalProjectVersionIdsOfSelectedProjects = useSelector(getOriginalProjectVersionIdsOfSelectedProjects)
  const projectsWithLanguageSelected = useSelector(getProjectsWithLanguageSelected)
  const allSelectedProjectsBelongToTtsProjects = useSelector(getAllSelectedProjectsBelongToTtsProjects)
  const allSelectedProjectsAllowToAddLanguages = useSelector(getAllSelectedProjectsAllowToAddLanguages)
  const allSelectedProjectsAllowToGenerateClips = useSelector(getAllSelectedProjectsAllowToGenerateClips)
  const allSelectedProjectsCanManageSharing = useSelector(getAllSelectedProjectsCanManageSharing)
  const workspaces = useSelector(getWorkspaces)
  const userIsAccountAdmin = useSelector(getUserIsAccountAdmin)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)

  const numberOfSelectedProjects = selectedProjectIds.length
  const shouldAllowToAddLanguages = (numberOfSelectedProjects > 1 && !projectsWithLanguageSelected || numberOfSelectedProjects === 1) && allSelectedProjectsAllowToAddLanguages
  const shouldAllowToGenerateClips = (numberOfSelectedProjects > 1 && !projectsWithLanguageSelected || numberOfSelectedProjects === 1) && allSelectedProjectsAllowToGenerateClips

  const tableElement = document.getElementsByClassName('ProjectsTable')[0] as HTMLDivElement | undefined
  const sidebarElement = document.getElementsByClassName('Sidebar')[0] as HTMLDivElement | undefined

  const tableWidth = tableElement?.offsetWidth || 0
  const sidebarWidth = sidebarElement?.offsetWidth || 0

  const hasMoreThanOneWorkspace = workspaces.length > 0

  const style = {
    left: `${sidebarWidth + 0.5 * (tableWidth - BAR_WIDTH)}px`,
  }

  useEffect(() => {
    if (!tableElement || !sidebarElement) {
      handleClose()
    }
  }, [])

  useClickOutsideListener('ProjectsBulkActionBar__more-wrapper', setShowMoreDropdown, showMoreDropdown)

  const handleClose = () => {
    dispatch(unselectProjects())
  }

  const handleAddLanguages = () => {
    if (currentAccountHasCurrentSubscription) {
      allSelectedProjectsBelongToTtsProjects ? dispatch(openSetupProjectPopupTts({ projectVersionIds: originalProjectVersionIdsOfSelectedProjects })) : dispatch(openSetupProjectPopup({ projectVersionIds: originalProjectVersionIdsOfSelectedProjects }))
    } else {
      dispatch(openRenewSubscriptionPopup())
    }
  }

  const handleGenerateClips = () => {
    if (currentAccountHasCurrentSubscription) {
      dispatch(openSetupProjectPopup({ projectVersionIds: originalProjectVersionIdsOfSelectedProjects, projectType: 'repurpose' }))
    } else {
      dispatch(openRenewSubscriptionPopup())
    }
  }

  const handleOpenLabelsManagerPopup = () => {
    dispatch(openLabelsManagerPopup())
  }

  const handleToggleMoreDropdown = () => {
    setShowMoreDropdown(!showMoreDropdown)
  }

  const handleOpenExportationPopup = () => {
    dispatch(openExportationPopup({ projectIds: selectedProjectIds }))
  }

  const handleOpenProjectSharingPopup = () => {
    dispatch(openProjectSharingPopup({ projectIds: selectedProjectIds, includePublicLink: false }))
  }

  const handleChangeWorkspace = () => {
    dispatch(openMoveToOtherWorkspacePopup())
    setShowMoreDropdown(false)
  }

  const handleDelete = () => {
    const payload = { projectIds: selectedProjectIds }
    dispatch(openDeleteProjectPopup(payload))
  }

  const renderGenerateClips = () => {
    return (
      <Button
        caption="✨ Create clips"
        styleType="brand-subtle"
        size="small"
        onClick={handleGenerateClips}
      />
    )
  }

  const renderAddLanguages = () => {
    return (
      <Button
        caption="Add languages"
        icon={<Add />}
        styleType="gray-subtle"
        size="small"
        onClick={handleAddLanguages}
      />
    )
  }

  const renderManageLabels = () => {
    return (
      <Button
        icon={<EditLabel />}
        styleType="gray-subtle"
        size="small"
        onClick={handleOpenLabelsManagerPopup}
      />
    )
  }

  const renderExport = () => {
    return (
      <div className="ProjectsBulkActionBar__exportation-wrapper">
        <Button
          caption="Export"
          icon={<Exportation />}
          styleType="gray-subtle"
          size="small"
          onClick={handleOpenExportationPopup}
        />
      </div>
    )
  }

  const renderShare = () => {
    return (
      <Button
        caption="Share"
        icon={<Share />}
        styleType="gray-subtle"
        size="small"
        onClick={handleOpenProjectSharingPopup}
        data-testid="bulk-project-share-action"
      />
    )
  }

  const renderDelete = () => {
    return (
      <Button
        icon={<Delete />}
        styleType="danger"
        size="small"
        onClick={handleDelete}
      />
    )
  }

  const renderMore = () => {
    return (
      <div className="ProjectsBulkActionBar__more-wrapper">
        <Button
          caption=""
          icon={<More />}
          styleType="gray-subtle"
          size="small"
          onClick={handleToggleMoreDropdown}
        />
        {showMoreDropdown && renderMoreDropdown()}
      </div>
    )
  }

  const renderMoreDropdown = () => {
    return (
      <ul className="ProjectsBulkActionBar__more-dropdown">
        <li
          className="ProjectsBulkActionBar__more-dropdown-item"
          onClick={handleChangeWorkspace}
        >
          <Move />
          Change workspace
        </li>
      </ul>
    )
  }

  return (
    <div
      className="ProjectsBulkActionBar"
      style={style}
    >
      <div className="ProjectsBulkActionBar__left">
        <Close
          onClick={handleClose}
        />
        <div className="ProjectsBulkActionBar__selected">
          {numberOfSelectedProjects === 1 ? '1 project selected' : `${numberOfSelectedProjects} projects selected`}
        </div>
      </div>
      <div className="ProjectsBulkActionBar__right">
        {shouldAllowToGenerateClips && renderGenerateClips()}
        {shouldAllowToAddLanguages && renderAddLanguages()}
        {allSelectedProjectsCanManageSharing && renderShare()}
        {renderExport()}
        {renderManageLabels()}
        {hasMoreThanOneWorkspace && userIsAccountAdmin && renderMore()}
        {renderDelete()}
      </div>
    </div>
  )
}

export default ProjectsBulkActionBar
